import {
  BadgeCheck,
  Bell,
  Bike,
  CalendarDays,
  Clock3,
  Info,
  LogIn,
  Mail,
  Package,
  Phone,
  Power,
  PowerOff,
  UserPlus,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

const InfoBadge = ({ children, color = "gray" }) => {
  const styles = {
    gray: "bg-gray-100 text-gray-600",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
    orange: "bg-orange-100 text-orange-700",
    red: "bg-red-100 text-red-700",
    purple: "bg-purple-100 text-purple-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
        styles[color]
      }`}
    >
      {children}
    </span>
  );
};

const DriversTable = ({
  currentDrivers = [],

  loading,

  year,
  month,

  packerUpdatingId,

  getDriverCode,
  getDriverPackerStatus,
  handlePackerStatusChange,

  toggleDutyStatus,

  formatDateTime,

  setNotificationDriver,
  setNotificationModalOpen,

  setDetailsDriver,
  setDetailsModalOpen,

  setStatusDriver,
  setStatusConfirmOpen,

  setSelectedDriverForTeams,
  setAssignTeamDriverObj,
  setAssignTeamsOpen,
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex-1 min-h-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="h-full overflow-y-auto">
        <table className="min-w-full">
          {/* =================================================
              HEADER
          ================================================= */}

          <thead className="sticky top-0 z-20 bg-orange-400 text-white border-b border-gray-200">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Driver
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Contact
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Vehicle / Shift
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Team / Type
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Status
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Packer
              </th>

              <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Activity
              </th>

              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>

          {/* =================================================
              BODY
          ================================================= */}

          <tbody className="divide-y divide-gray-100 bg-white">
            {currentDrivers?.map((driver) => {
              const driverCode = getDriverCode(driver);

              const packerStatus = getDriverPackerStatus(driver);

              const isPacker = packerStatus === "Y";

              const isPackerUpdating = packerUpdatingId === driverCode;

              return (
                <tr
                  key={driver.id || driver.driverId || driver.driver_code}
                  className="align-top transition-colors duration-200 hover:bg-orange-50/40"
                >
                  {/* =================================================
                      DRIVER
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={driver.profile_photo || "/placeholder-driver.png"}
                        alt={driver.fullName || "Driver"}
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://ui-avatars.com/api/?name=" +
                            encodeURIComponent(driver.fullName || "Driver") +
                            "&background=F3F4F6&color=374151";
                        }}
                        className="h-11 w-11 rounded-xl border border-gray-200 object-cover shrink-0"
                      />

                      <div className="min-w-0">
                        <div
                          className="flex items-center gap-2 flex-wrap"
                          onClick={() => {
                            const driverId = driver.driverId;

                            const username = encodeURIComponent(
                              driver.fullName || driver.driverId,
                            );

                            const monthStart = `${year}-${String(
                              month + 1,
                            ).padStart(2, "0")}-01`;

                            const todayDate = new Date()
                              .toISOString()
                              .split("T")[0];

                            navigate(`/analytics/${driverId}/${username}`, {
                              state: {
                                fromDate: monthStart,
                                toDate: todayDate,
                              },
                            });
                          }}
                        >
                          <p className="text-sm font-semibold text-gray-900 leading-none truncate cursor-pointer underline">
                            {driver.fullName || "-"}
                          </p>

                          <InfoBadge color="blue">
                            {driver.driverId || "-"}
                          </InfoBadge>
                        </div>

                        <p className="mt-1 text-xs text-gray-500 truncate">
                          @{driver.username || "-"}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      CONTACT
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2 min-w-0">
                        <Phone size={13} className="text-gray-400 shrink-0" />

                        <span className="truncate">{driver.phone || "-"}</span>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <Mail size={13} className="text-gray-400 shrink-0" />

                        <span className="truncate">{driver.email || "-"}</span>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      VEHICLE / SHIFT
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2 min-w-0">
                        <Bike size={13} className="text-gray-400 shrink-0" />

                        <span className="truncate">
                          {driver.transport_type || "-"}

                          {driver.transport_description
                            ? ` • ${driver.transport_description}`
                            : ""}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <BadgeCheck
                          size={13}
                          className="text-gray-400 shrink-0"
                        />

                        <span className="truncate">
                          {driver.licence_plate || "-"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <Clock3 size={13} className="text-gray-400 shrink-0" />

                        <span>
                          {driver.shift_start || "-"} -{" "}
                          {driver.shift_end || "-"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      TEAM / TYPE
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <InfoBadge color="orange">
                        {driver.teamName || "Unassigned"}
                      </InfoBadge>

                      <InfoBadge color="green">
                        {driver.driver_type || "N/A"}
                      </InfoBadge>

                      <InfoBadge color="purple">
                        {driver.tier || "N/A"}
                      </InfoBadge>
                    </div>
                  </td>

                  {/* =================================================
                      DUTY STATUS
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <label
                          className={`relative inline-flex items-center ${
                            loading
                              ? "opacity-60 pointer-events-none"
                              : "cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={driver.dutyStatus === "active"}
                            onChange={() =>
                              toggleDutyStatus(
                                driver,
                                driver.dutyStatus === "active",
                              )
                            }
                            disabled={loading}
                          />

                          <div className="h-5 w-9 rounded-full bg-gray-300 transition-colors peer-checked:bg-green-500" />

                          <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                        </label>

                        <span className="text-[11px] font-medium text-gray-600">
                          {driver.dutyStatus === "active"
                            ? "On duty"
                            : "Off duty"}
                        </span>
                      </div>

                      <div>
                        <InfoBadge
                          color={driver.status === "active" ? "green" : "red"}
                        >
                          {driver.status || "-"}
                        </InfoBadge>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      PACKER
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5">
                      <button
                        type="button"
                        onClick={() => handlePackerStatusChange(driver)}
                        disabled={isPackerUpdating || !driverCode}
                        title={isPacker ? "Disable Packer" : "Enable Packer"}
                        className={`relative h-5 w-9 rounded-full transition-colors ${
                          isPacker ? "bg-orange-500" : "bg-gray-300"
                        } ${
                          isPackerUpdating
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                            isPacker ? "left-4" : "left-0.5"
                          }`}
                        />
                      </button>

                      <span
                        className={`text-[11px] font-medium ${
                          isPacker ? "text-orange-600" : "text-gray-500"
                        }`}
                      >
                        {isPackerUpdating
                          ? "Updating..."
                          : isPacker
                            ? "Packer"
                            : "Not Packer"}
                      </span>
                    </div>
                  </td>

                  {/* =================================================
                      ACTIVITY
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center gap-2">
                        <CalendarDays
                          size={13}
                          className="text-gray-400 shrink-0"
                        />

                        <span className="truncate">
                          {formatDateTime(driver.joiningDate)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <LogIn size={13} className="text-gray-400 shrink-0" />

                        <span className="truncate">
                          {formatDateTime(driver.last_login)}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* =================================================
                      ACTIONS
                  ================================================= */}

                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      {/* NOTIFICATION */}

                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-green-600 transition hover:bg-green-50"
                        onClick={() => {
                          setNotificationDriver(driver);

                          setNotificationModalOpen(true);
                        }}
                        title="Notifications"
                      >
                        <Bell size={14} />
                      </button>

                      {/* DETAILS */}

                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50"
                        onClick={() => {
                          setDetailsDriver(driver);

                          setDetailsModalOpen(true);
                        }}
                        title="Driver Details"
                      >
                        <Info size={14} />
                      </button>

                      {/* ACTIVE / DEACTIVE */}

                      <button
                        type="button"
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-white transition ${
                          driver.status === "active"
                            ? "border-red-200 text-red-600 hover:bg-red-50"
                            : "border-green-200 text-green-600 hover:bg-green-50"
                        }`}
                        onClick={() => {
                          setStatusDriver(driver);

                          setStatusConfirmOpen(true);
                        }}
                        title={
                          driver.status === "active"
                            ? "Deactivate Driver"
                            : "Activate Driver"
                        }
                      >
                        {driver.status === "active" ? (
                          <PowerOff size={14} />
                        ) : (
                          <Power size={14} />
                        )}
                      </button>

                      {/* ASSIGN TEAM */}

                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-blue-600 transition hover:bg-blue-50"
                        onClick={() => {
                          setSelectedDriverForTeams(driver.driverId);

                          setAssignTeamDriverObj(driver);

                          setAssignTeamsOpen(true);
                        }}
                        title="Assign Teams"
                      >
                        <UserPlus size={14} />
                      </button>

                      {/* ORDERS */}

                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-orange-600 transition hover:bg-orange-50"
                        onClick={() =>
                          navigate(`/drivers/orders/${driver.driverId}`, {
                            state: {
                              driver,
                            },
                          })
                        }
                        title="View Driver Orders"
                      >
                        <Package size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DriversTable;
